import React, { useEffect, useRef } from 'react';
import { Box, Button, Container, Flex, Grid, HStack, Icon, Stack, Text, useColorMode, Wrap, WrapItem } from '@chakra-ui/react';
import { FiArrowLeft, FiArrowRight, FiCalendar, FiCheck, FiCheckCircle, FiClock, FiDollarSign, FiMapPin, FiPhone, FiVideo } from 'react-icons/fi';
import { addDays, format, isSameDay, parseISO } from 'date-fns';
import { SunshineBackground } from './SunshineBackground';
import { getColor } from '../../../brandConfig';
import { Tooltip } from '../components/Tip';
import { alpha, calWords, CalError, CalLoading, CalSectionHeading, CalStepper, CalSurface, useCalTheme } from '../components/ui';

/**
 * The public booking pages' shared parts (POLISH v1, 2026-09-25): the page shell, the four-step
 * progress line, the week-of-times picker and the "your choice" card. The event-type page and the
 * main booking page used to carry two drifting copies of the picker; this is the one copy.
 * A first-time visitor on a phone is the reader: 16px text, 44px+ targets, words on every button.
 */

export interface BookingTimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
}

export interface BookingEventTypeLike {
  name: string;
  durationMinutes: number;
  isPaid: boolean;
  price?: number;
  currency?: string;
  locationType: string;
  location?: string;
}

/** `locationType` in plain words — accepts any casing ("virtual", "Virtual"). */
export const locationWords = (type?: string) => {
  if (!type) return '';
  const key = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  return calWords('location', key);
};

export const locationIcon = (type?: string) => {
  const t = (type || '').toLowerCase();
  return t === 'virtual' ? FiVideo : t === 'phone' ? FiPhone : FiMapPin;
};

/** "$150 USD", or "Free". */
export const priceWords = (et: Pick<BookingEventTypeLike, 'isPaid' | 'price' | 'currency'>) =>
  et.isPaid && et.price ? `$${et.price}${et.currency ? ` ${et.currency}` : ''}` : 'Free';

/** The page behind every public booking screen — brand background, the sunshine rays, a phone-safe gutter. */
export function BookingShell({ children, maxW = 'container.lg', sunshine = true }: { children: React.ReactNode; maxW?: string; sunshine?: boolean }) {
  const { colorMode } = useColorMode();
  return (
    <Box minH="100vh" position="relative" bg={getColor('background.main', colorMode)} overflowX="hidden">
      {sunshine && <SunshineBackground />}
      <Box position="relative" zIndex={1}>
        <Container maxW={maxW} px={4} py={{ base: 5, md: 10 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
}

const STEPS = [
  { key: 'choose', label: 'Choose' },
  { key: 'time', label: 'Pick a time' },
  { key: 'details', label: 'Your details' },
  { key: 'done', label: 'Booked' },
];

/** Where the visitor is in the four steps. `done` = steps finished. */
export function BookingSteps({ done }: { done: number }) {
  return (
    <CalSurface py={{ base: 3, md: 4 }}>
      <CalStepper steps={STEPS} done={done} size="sm" />
    </CalSurface>
  );
}

/** A small fact chip: icon + words, 14px, never colour alone. */
export function BookingFact({ icon, children, testId }: { icon: React.ElementType; children: React.ReactNode; testId?: string }) {
  const t = useCalTheme();
  return (
    <HStack as="span" spacing={1.5} px={2.5} py={1} borderRadius="full" bg={t.raised} borderWidth="1px" borderColor={t.border} display="inline-flex">
      <Icon as={icon} color={t.primary} boxSize={4} data-testid={testId} />
      <Box as="span" fontSize="sm" fontWeight="semibold" color={t.text}>{children}</Box>
    </HStack>
  );
}

interface WeekPickerProps {
  weekStart: Date;
  selectedTime: BookingTimeSlot | null;
  slotsByDay: Record<string, BookingTimeSlot[]>;
  loading: boolean;
  error?: { message?: string } | null;
  onRetry?: () => unknown;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onPick: (day: Date, slot: BookingTimeSlot) => void;
  /** Full-width page: seven day columns on wide screens. */
  wide?: boolean;
}

/** One week of bookable times: loading, failed, empty and full are four different screens. */
export function WeekSlotPicker({ weekStart, selectedTime, slotsByDay, loading, error, onRetry, onPrevWeek, onNextWeek, onPick, wide }: WeekPickerProps) {
  const t = useCalTheme();
  const weekEnd = addDays(weekStart, 6);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const total = days.reduce((n, d) => n + (slotsByDay[format(d, 'yyyy-MM-dd')] || []).filter((s) => s.available).length, 0);
  let zone = '';
  try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* older browsers */ }

  return (
    <CalSurface>
      <CalSectionHeading icon={FiCalendar} title="Pick a time"
        why={`Times are shown in your own time zone${zone ? ` (${zone})` : ''}. Nothing is booked until you confirm.`} />

      <Stack direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }} justify="space-between" spacing={3} mb={5}>
        <Text order={{ base: 0, md: 1 }} fontSize="lg" fontWeight="bold" color={t.text} textAlign="center">
          {format(weekStart, 'd MMM')} – {format(weekEnd, 'd MMM yyyy')}
        </Text>
        <Box order={{ base: 1, md: 0 }} flex={{ base: 1, md: 'none' }}>
          <Tooltip label="Show the week before. Nothing is booked.">
            <Button w="full" minH="48px" size="lg" variant="outline" leftIcon={<Icon as={FiArrowLeft} />} onClick={onPrevWeek}>
              Previous week
            </Button>
          </Tooltip>
        </Box>
        <Box order={2} flex={{ base: 1, md: 'none' }}>
          <Tooltip label="Show the week after. Nothing is booked.">
            <Button w="full" minH="48px" size="lg" variant="outline" rightIcon={<Icon as={FiArrowRight} />} onClick={onNextWeek}>
              Next week
            </Button>
          </Tooltip>
        </Box>
      </Stack>

      {loading ? (
        <CalLoading label="Loading free times…" cards={3} />
      ) : error ? (
        <CalError title="We couldn't load the free times" error={error} onRetry={onRetry} />
      ) : (
        <>
          {total === 0 && (
            <HStack spacing={3} p={4} mb={4} borderRadius="xl" bg={alpha(t.warning, 0.1)} borderWidth="1px" borderColor={alpha(t.warning, 0.3)} align="start">
              <Icon as={FiClock} color={t.warning} mt={1} flexShrink={0} />
              <Text fontSize="md" color={t.text}>No free times this week. Try <b>Next week</b>.</Text>
            </HStack>
          )}
          <Grid templateColumns={wide
            ? { base: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)', xl: 'repeat(7, 1fr)' }
            : { base: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }} gap={3}>
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const slots = slotsByDay[key] || [];
              const isToday = isSameDay(day, new Date());
              const holdsChoice = !!selectedTime && slots.some((s) => s.startTime === selectedTime.startTime);
              return (
                <Box key={key} p={3} borderRadius="xl" borderWidth={holdsChoice ? '2px' : '1px'} minW={0}
                  borderColor={holdsChoice ? t.primary : t.border} bg={holdsChoice ? alpha(t.primary, 0.06) : t.raised}>
                  <HStack justify="space-between" mb={slots.length ? 3 : 1} flexWrap="wrap" spacing={2}>
                    <Text fontSize="md" fontWeight="bold" color={t.text}>{format(day, 'EEE d MMM')}</Text>
                    {isToday && <Text as="span" fontSize="sm" fontWeight="semibold" color={t.primary}>Today</Text>}
                  </HStack>
                  {slots.length === 0 ? (
                    <Text fontSize="sm" color={t.faint}>No free times</Text>
                  ) : (
                    <Wrap spacing={2}>
                      {slots.map((slot) => {
                        const chosen = selectedTime?.startTime === slot.startTime;
                        const label = format(parseISO(slot.startTime), 'h:mm a');
                        return (
                          <WrapItem key={slot.startTime} flexGrow={1} minW="96px">
                            {/* A Box, not shouldWrapChildren: the auto-span is inline-block and would shrink the button. */}
                            <Tooltip label={slot.available ? `Choose ${label} on ${format(day, 'EEEE')}. Nothing is booked until you confirm.` : 'This time has been taken.'}>
                              <Box w="full">
                              <Button w="full" minH="44px" fontSize="md" variant={chosen ? 'solid' : 'outline'}
                                bg={chosen ? t.primary : undefined} color={chosen ? 'white' : t.text} borderColor={chosen ? t.primary : t.borderStrong}
                                _hover={chosen ? { bg: t.primary, opacity: 0.92 } : { borderColor: t.primary }}
                                leftIcon={chosen ? <Icon as={FiCheck} /> : undefined}
                                aria-pressed={chosen}
                                onClick={() => onPick(day, slot)} isDisabled={!slot.available}>
                                {label}
                              </Button>
                              </Box>
                            </Tooltip>
                          </WrapItem>
                        );
                      })}
                    </Wrap>
                  )}
                </Box>
              );
            })}
          </Grid>
        </>
      )}
    </CalSurface>
  );
}

/** The visitor's pick, restated in words, and the page's one primary action. Scrolls itself into view on a phone. */
export function SelectedTimeCard({ slot, eventType, onContinue }: { slot: BookingTimeSlot; eventType?: BookingEventTypeLike; onContinue: () => void }) {
  const t = useCalTheme();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [slot.startTime]);
  const start = parseISO(slot.startTime);
  const end = parseISO(slot.endTime);
  return (
    <Box ref={ref}>
      <CalSurface borderWidth="2px" borderColor={t.primary} bg={alpha(t.primary, t.colorMode === 'dark' ? 0.12 : 0.05)}>
        <CalSectionHeading icon={FiCheckCircle} tone="success" title="Your choice" why="Check it's right, then add your details on the next step." />
        <Stack spacing={2} mb={5}>
          <HStack spacing={3} align="start">
            <Icon as={FiCalendar} color={t.primary} boxSize={5} mt={1} flexShrink={0} />
            <Text fontSize="lg" fontWeight="bold" color={t.text}>{format(start, 'EEEE d MMMM yyyy')}</Text>
          </HStack>
          <HStack spacing={3} align="start">
            <Icon as={FiClock} color={t.primary} boxSize={5} mt={1} flexShrink={0} />
            <Text fontSize="lg" fontWeight="bold" color={t.text}>{format(start, 'h:mm a')} – {format(end, 'h:mm a')}</Text>
          </HStack>
          {eventType && (
            <Wrap spacing={2} pt={1}>
              <WrapItem><Text fontSize="md" fontWeight="semibold" color={t.text} wordBreak="break-word">{eventType.name}</Text></WrapItem>
              <WrapItem><BookingFact icon={FiDollarSign}>{priceWords(eventType)}</BookingFact></WrapItem>
            </Wrap>
          )}
        </Stack>
        <Tooltip label="Go to the last step to enter your name and email. Nothing is booked yet.">
          <Button w="full" size="lg" minH="56px" fontSize="lg" bgImage={t.accentGradient} color="white"
            _hover={{ opacity: 0.92 }} _active={{ opacity: 0.85 }} rightIcon={<Icon as={FiArrowRight} />} onClick={onContinue}>
            Continue to book
          </Button>
        </Tooltip>
      </CalSurface>
    </Box>
  );
}

/** A labelled fact row for detail cards: icon, small label, the value in body size. */
export function DetailRow({ icon, label, children }: { icon: React.ElementType; label: string; children: React.ReactNode }) {
  const t = useCalTheme();
  return (
    <Flex gap={3} align="start" minW={0}>
      <Icon as={icon} color={t.primary} boxSize={5} mt={1} flexShrink={0} />
      <Box minW={0} flex={1}>
        <Text fontSize="sm" color={t.faint}>{label}</Text>
        <Box fontSize="md" fontWeight="semibold" color={t.text} wordBreak="break-word">{children}</Box>
      </Box>
    </Flex>
  );
}
